import { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Switch, Table, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitGroupDto, DataKitItemDto } from "../../../../domain/DataKitDto";
import "./DataKitItemsTab.scss";

const ALL_GROUPS = "__all__";

export function formatGroupEntry(entry: { groupName: string; groupPath?: string[] }): string {
  const path = entry.groupPath || [];
  return path.length > 0 ? path.join(" › ") : entry.groupName;
}

/**
 * Items no longer have a primary group, so the Group column lists every entry rather than picking
 * one. The first entry is what the canvas renders, so it is shown first and the rest follow.
 */
export function formatGroups(item: DataKitItemDto): string[] {
  return item.groups.map(formatGroupEntry);
}

export function formatPrices(item: DataKitItemDto): string {
  const prices = [...item.prices].sort((a, b) => a.sequence - b.sequence);
  const shown = prices.map((p) => p.price).filter((p) => !!p && p.trim() !== "");
  if (shown.length === 0) return "––";
  return shown.join(" / ");
}

/**
 * How many locations and menus price this item themselves.
 *
 * Counted as distinct locations and menus, not as override rows: an item with two sizes overridden
 * at one location is "1 location", not 2. The old chip read "3 ovr", which told a member neither
 * what was overridden nor by whom - and "ovr" is the internal word for it, which customer copy
 * never uses.
 */
export function overrideSummary(item: DataKitItemDto): string | null {
  const locations = new Set<string>();
  const menus = new Set<string>();
  item.prices.forEach((p) => {
    (p.overrides || []).forEach((o) => locations.add(o.unitId));
    (p.menuOverrides || []).forEach((o) => menus.add(o.menuId));
  });
  const parts: string[] = [];
  if (locations.size > 0) {
    parts.push(`${locations.size} location${locations.size === 1 ? "" : "s"}`);
  }
  if (menus.size > 0) parts.push(`${menus.size} menu${menus.size === 1 ? "" : "s"}`);
  return parts.length > 0 ? parts.join(" \u00b7 ") : null;
}

interface Props {
  kit: DataKitDto;
  items: DataKitItemDto[];
  groups: DataKitGroupDto[];
  archivedCount: number;
  showArchived: boolean;
  onToggleArchived: () => void;
  canWrite: boolean;
  onChanged: () => void;
  onEditItem: (item: DataKitItemDto) => void;
}

export function DataKitItemsTab({
  kit,
  items,
  groups,
  archivedCount,
  showArchived,
  onToggleArchived,
  canWrite,
  onChanged,
  onEditItem,
}: Props) {
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>(ALL_GROUPS);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  useEffect(() => {
    setSelected([]);
  }, [showArchived]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (groupFilter !== ALL_GROUPS && !item.groups.some((g) => g.groupName === groupFilter)) {
        return false;
      }
      if (q && item.groups.some((g) => formatGroupEntry(g).toLowerCase().includes(q))) return true;
      if (!q) return true;
      return (
        (item.name || "").toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q) ||
        item.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [items, query, groupFilter]);

  const setAvailability = (item: DataKitItemDto, available: boolean) => {
    if (!kit.id || !item.id) return;
    setBusy(true);
    DataKitApi.setAvailability(kit.id, { itemIds: [item.id], available })
      .then(onChanged)
      .catch(() => message.error("Could not change availability"))
      .finally(() => setBusy(false));
  };

  const bulkArchive = () => {
    if (!kit.id || selected.length === 0) return;
    setBusy(true);
    DataKitApi.bulkArchiveItems(kit.id, { itemIds: selected })
      .then(() => {
        setSelected([]);
        setConfirmArchive(false);
        onChanged();
      })
      .catch(() => message.error("Could not archive the selected items"))
      .finally(() => setBusy(false));
  };

  const bulkRestore = () => {
    if (!kit.id || selected.length === 0) return;
    setBusy(true);
    DataKitApi.bulkRestoreItems(kit.id, { itemIds: selected })
      .then(() => {
        setSelected([]);
        onChanged();
      })
      .catch(() => message.error("Could not restore the selected items"))
      .finally(() => setBusy(false));
  };

  const bulkAvailability = (available: boolean) => {
    if (!kit.id || selected.length === 0) return;
    setBusy(true);
    DataKitApi.setAvailability(kit.id, { itemIds: selected, available })
      .then(() => {
        setSelected([]);
        onChanged();
      })
      .catch(() => message.error("Could not change availability"))
      .finally(() => setBusy(false));
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      sorter: (a: DataKitItemDto, b: DataKitItemDto) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
      render: (_: string, item: DataKitItemDto) => (
        <div className="data-kit-item-name-cell">
          <div className="data-kit-item-name">{item.name || "Untitled"}</div>
          {item.description && (
            <div className="data-kit-item-description">{item.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "Group",
      key: "group",
      width: 220,
      render: (_: unknown, item: DataKitItemDto) => {
        const entries = formatGroups(item);
        if (entries.length === 0) return <span className="data-kit-item-no-group">––</span>;
        return (
          <div className="data-kit-item-groups">
            {entries.map((label) => (
              <span key={label} className="data-kit-item-group-chip">
                {label}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Price",
      key: "price",
      width: 170,
      align: "center" as const,
      render: (_: unknown, item: DataKitItemDto) => {
        const summary = overrideSummary(item);
        return (
          <div className="data-kit-item-price-cell">
            <span>{formatPrices(item)}</span>
            {summary && <span className="data-kit-override-chip">{summary}</span>}
          </div>
        );
      },
    },
    {
      title: "Used on",
      key: "usedOn",
      width: 140,
      render: (_: unknown, item: DataKitItemDto) =>
        item.usedOnCount > 0
          ? `${item.usedOnCount} menu${item.usedOnCount === 1 ? "" : "s"}`
          : "––",
    },
    {
      title: "Available",
      key: "available",
      width: 110,
      align: "right" as const,
      onHeaderCell: () => ({ title: "Applies immediately — no publish needed" }),
      render: (_: unknown, item: DataKitItemDto) => (
        <Switch
          className="data-kit-available-switch"
          size="small"
          checked={item.available}
          disabled={!canWrite || busy || item.archived}
          onClick={(_checked, e) => e.stopPropagation()}
          onChange={(checked) => setAvailability(item, checked)}
        />
      ),
    },
  ];

  return (
    <div className="data-kit-items-tab">
      <div className="data-kit-items-toolbar">
        <Input
          className="data-kit-items-search"
          size="large"
          placeholder={`Search ${items.length} item${items.length === 1 ? "" : "s"}`}
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
        />
        <Select
          className="data-kit-group-filter"
          size="large"
          value={groupFilter}
          onChange={setGroupFilter}
          options={[
            { label: "All groups", value: ALL_GROUPS },
            ...groups.map((g) => ({ label: g.name, value: g.name })),
          ]}
        />
        <button type="button" className="data-kit-archived-link" onClick={onToggleArchived}>
          {showArchived ? "Back to active items" : `Archived • ${archivedCount}`}
        </button>
      </div>

      {selected.length > 0 && (
        <div className="data-kit-bulk-toolbar">
          <span className="data-kit-bulk-count">
            {selected.length} selected
          </span>
          <div className="data-kit-bulk-actions">
            {showArchived ? (
              <Button size="small" onClick={bulkRestore} disabled={busy}>
                Restore
              </Button>
            ) : (
              <>
                <Button size="small" onClick={() => bulkAvailability(true)} disabled={busy}>
                  Mark available
                </Button>
                <Button size="small" onClick={() => bulkAvailability(false)} disabled={busy}>
                  Mark 86'd
                </Button>
                <Button size="small" danger onClick={() => setConfirmArchive(true)} disabled={busy}>
                  Archive
                </Button>
              </>
            )}
            <Button size="small" type="text" onClick={() => setSelected([])}>
              Clear
            </Button>
          </div>
        </div>
      )}

      <Table
        className="data-kit-items-table"
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        rowClassName="data-kit-item-row"
        pagination={false}
        rowSelection={
          canWrite
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys as string[]),
              }
            : undefined
        }
        locale={{
          emptyText: showArchived
            ? "No archived items"
            : query.trim() || groupFilter !== ALL_GROUPS
            ? "No items match your filters"
            : "No items in this kit yet",
        }}
        onRow={(item) => ({
          onClick: () => onEditItem(item),
        })}
      />

      <Modal
        className={"clean one"}
        open={confirmArchive}
        onCancel={() => setConfirmArchive(false)}
      >
        <header>
          <h2>
            Archive {selected.length} item{selected.length === 1 ? "" : "s"}?
          </h2>
        </header>
        <p className="data-kit-archive-copy">
          Archiving is staged: these items keep appearing on designs until you publish this kit.
          To take something off a menu right now, switch it to unavailable instead — that applies
          immediately, without publishing anything else. You can restore archived items from the
          archived view.
        </p>
        <footer>
          <Button size="large" onClick={() => setConfirmArchive(false)}>
            Cancel
          </Button>
          <Button size="large" danger loading={busy} onClick={bulkArchive}>
            Archive
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
